import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { NumericInput } from '@components/core-ui/form-elements/NumericInput';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { buildAsrModelOptions, DEFAULT_ASR_MODEL_VALUE } from '@helpers/asrProxy/buildAsrModelOptions';
import { getAsrModelCapabilities } from '@helpers/asrProxy/getAsrModelCapabilities';
import { cn } from '@helpers/cn';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { createResubmitAdminJobMutationFn } from '@modals/Admin/AdminResubmitJobModal/api/resubmitAdminJob';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { createFetchAdminJobQueryFn } from '@pages/Admin/JobDetailsPage/api/fetchAdminJob';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminJobsKeys } from 'app/queries/adminJobs/adminJobsKeys';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { createFetchAsrResubmitModelOptionsQueryFn } from 'app/queries/asrProxy/fetchAsrResubmitModelOptions';
import { useState } from 'react';

type TProps = {
  onCancel: () => void;
  onSuccess: () => void;
  id: string;
};

export const AdminResubmitJobModal = ({ onCancel, onSuccess, id }: TProps) => {
  const [rushedJobProcessing, setRushedJobProcessing] = useState<boolean>(false);
  const [clearChunksS3Cache, setClearChunksS3Cache] = useState<boolean>(false);
  const [forcePreprocessing, setForcePreprocessing] = useState<boolean>(false);
  const [transcriptionModel, setTranscriptionModel] = useState<string>(DEFAULT_ASR_MODEL_VALUE);
  const [useTwoStageClustering, setUseTwoStageClustering] = useState<'default' | 'enabled' | 'disabled'>('default');
  const [speakerCountMode, setSpeakerCountMode] = useState<'default' | 'exact' | 'range'>('default');
  const [speakersExpected, setSpeakersExpected] = useState<number>(2);
  const [minSpeakersExpected, setMinSpeakersExpected] = useState<number>(2);
  const [maxSpeakersExpected, setMaxSpeakersExpected] = useState<number>(10);
  const [enhancedTranscriptMode, setEnhancedTranscriptMode] = useState<
    'auto' | 'force_asone' | 'force_assembly' | undefined
  >('auto');
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { call } = useAPI();
  const jobQuery = useQuery({
    queryKey: adminJobsKeys.detail(id),
    queryFn: createFetchAdminJobQueryFn(call),
    refetchOnMount: 'always',
  });
  const modelOptionsQuery = useQuery({
    queryKey: asrProxyKeys.resubmitModelOptions(),
    queryFn: createFetchAsrResubmitModelOptionsQueryFn(call),
  });
  const asrProxyDisabled = modelOptionsQuery.data?.asrProxyDisabled ?? false;
  const proxyModels = modelOptionsQuery.data?.models ?? [];
  const defaultModel = modelOptionsQuery.data?.defaultModel ?? '';
  const proxyModelsLoading = modelOptionsQuery.isFetching;
  const noShow = jobQuery.data?.noShow === true;
  const fieldsDisabled = noShow || !jobQuery.data || jobQuery.isError || jobQuery.isFetching;

  const resubmitMutation = useMutation({
    mutationFn: createResubmitAdminJobMutationFn(call),
    onMutate: () => {
      setSubmitError(null);
    },
    onSuccess,
    onError: (error) => {
      setSubmitError(getApiErrorMessage(error) || 'Unable to resubmit the job.');
    },
  });

  const modelOptions = buildAsrModelOptions({
    models: proxyModels,
    defaultModel,
    selectedModel: transcriptionModel,
  });
  const selectedModel = proxyModels.find(
    (model) => model.name === (transcriptionModel === DEFAULT_ASR_MODEL_VALUE ? defaultModel : transcriptionModel),
  );
  const { supportsSpeakerCount, supportsTwoStageClustering } = getAsrModelCapabilities(
    asrProxyDisabled ? undefined : selectedModel,
  );

  const modelHint = proxyModelsLoading
    ? 'Loading ASR proxy models'
    : modelOptionsQuery.isError
      ? 'Could not refresh the ASR proxy models. The last known list is shown.'
      : selectedModel && !selectedModel.healthy
        ? `The last health check failed${selectedModel.healthDetail ? `: ${selectedModel.healthDetail}` : ''}`
        : proxyModels.length
          ? 'Select a single ASR proxy model'
          : 'No ASR proxy models loaded';
  const isSpeakerRangeInvalid =
    supportsSpeakerCount && speakerCountMode === 'range' && minSpeakersExpected > maxSpeakersExpected;
  const twoStageClusteringOverride =
    supportsTwoStageClustering && speakerCountMode !== 'exact'
      ? useTwoStageClustering === 'enabled'
        ? true
        : useTwoStageClustering === 'disabled'
          ? false
          : undefined
      : undefined;
  const hasTranscriptionOverrides =
    !asrProxyDisabled &&
    (transcriptionModel !== DEFAULT_ASR_MODEL_VALUE ||
      twoStageClusteringOverride !== undefined ||
      (supportsSpeakerCount && speakerCountMode !== 'default'));

  return (
    <ModalWrapper width="400px">
      {jobQuery.isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Confirm</ModalTitle>
      <div className="flex flex-col gap-6 text-center [&_small]:text-[var(--color-text-muted)]">
        {noShow ? (
          <p role="alert" className="text-[var(--color-error-heading)]">
            To resubmit this job for processing, turn off the &apos;No Show&apos; attribute.
          </p>
        ) : null}
        {jobQuery.isError ? (
          <ErrorMessage error="Unable to check the job's No Show attribute. Close this modal and try again." />
        ) : null}
        <p>Are you sure you want to resubmit this job?</p>

        <fieldset
          disabled={fieldsDisabled || resubmitMutation.isPending}
          className={cn('min-w-0', (fieldsDisabled || resubmitMutation.isPending) && 'pointer-events-none')}
        >
          <div className="mb-8">
            <Select
              disabled={fieldsDisabled || resubmitMutation.isPending}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '2' },
              ]}
              label="Clear chunks and metadata S3 cache"
              onChange={(_, value) => setClearChunksS3Cache(value === '1')}
              value={clearChunksS3Cache ? '1' : '2'}
            />
            <small>Clear chunks and metadata S3 cache</small>
          </div>

          <div className="mb-8">
            <Select
              disabled={fieldsDisabled || resubmitMutation.isPending}
              options={[
                { label: 'Yes', value: '1' },
                { label: 'No', value: '2' },
              ]}
              label="Rushed job processing"
              onChange={(_, value) => setRushedJobProcessing(value === '1')}
              value={rushedJobProcessing ? '1' : '2'}
            />
            <small>Mark job as a rushed job to process faster in another queue</small>
          </div>

          <DisclosureSection
            title="Advanced options"
            open={advancedOpen}
            setOpenState={setAdvancedOpen}
            loading={false}
            titleClassName="!bg-[var(--color-surface)]"
          >
            {!asrProxyDisabled ? (
              <div className="mb-8 mt-6">
                <Select
                  options={modelOptions}
                  label="ASR proxy model"
                  onChange={(_, value) => setTranscriptionModel(value)}
                  value={transcriptionModel}
                  disabled={fieldsDisabled || resubmitMutation.isPending || proxyModelsLoading}
                />
                <small>{modelHint}</small>
              </div>
            ) : null}

            {supportsTwoStageClustering ? (
              <div className="mb-8 mt-6">
                <Select
                  options={[
                    { label: 'Default', value: 'default' },
                    { label: 'Yes', value: 'enabled' },
                    { label: 'No', value: 'disabled' },
                  ]}
                  label="Two-stage clustering"
                  onChange={(_, value) => setUseTwoStageClustering(value as 'default' | 'enabled' | 'disabled')}
                  value={speakerCountMode === 'exact' ? 'disabled' : useTwoStageClustering}
                  disabled={fieldsDisabled || resubmitMutation.isPending || speakerCountMode === 'exact'}
                />
                <small style={{ color: 'var(--color-text-muted)' }}>
                  {speakerCountMode === 'exact'
                    ? 'Two-stage clustering conflicts with an exact speaker count'
                    : 'Enable two-stage clustering'}
                </small>
              </div>
            ) : null}

            {supportsSpeakerCount ? (
              <div className="mb-8">
                <Select
                  disabled={fieldsDisabled || resubmitMutation.isPending}
                  options={[
                    { label: 'Not set', value: 'default' },
                    { label: 'Exact count', value: 'exact' },
                    { label: 'Min/max range', value: 'range' },
                  ]}
                  label="Expected speakers"
                  onChange={(_, value) => setSpeakerCountMode(value as 'default' | 'exact' | 'range')}
                  value={speakerCountMode}
                />
                <small style={{ color: 'var(--color-text-muted)' }}>
                  Send either an exact speaker count or a min/max range to the selected model
                </small>
              </div>
            ) : null}

            {supportsSpeakerCount && speakerCountMode === 'exact' ? (
              <div className="mb-8">
                <NumericInput
                  disabled={fieldsDisabled || resubmitMutation.isPending}
                  value={speakersExpected}
                  onChange={(value) => setSpeakersExpected(Math.round(value))}
                  label="Exact speaker count"
                  name="speakersExpected"
                  min={1}
                  max={10}
                  clampToRange
                />
              </div>
            ) : null}

            {supportsSpeakerCount && speakerCountMode === 'range' ? (
              <div className="mb-8 space-y-3">
                <NumericInput
                  disabled={fieldsDisabled || resubmitMutation.isPending}
                  value={minSpeakersExpected}
                  onChange={(value) => setMinSpeakersExpected(Math.round(value))}
                  label="Minimum speakers"
                  name="minSpeakersExpected"
                  min={1}
                  max={10}
                  clampToRange
                />
                <NumericInput
                  disabled={fieldsDisabled || resubmitMutation.isPending}
                  value={maxSpeakersExpected}
                  onChange={(value) => setMaxSpeakersExpected(Math.round(value))}
                  label="Maximum speakers"
                  name="maxSpeakersExpected"
                  min={1}
                  max={10}
                  clampToRange
                />
                {isSpeakerRangeInvalid ? (
                  <small className="text-[var(--color-error-heading)]">
                    Minimum speakers cannot be greater than maximum speakers
                  </small>
                ) : null}
              </div>
            ) : null}

            <div className="mb-8">
              <Select
                disabled={fieldsDisabled || resubmitMutation.isPending}
                options={[
                  { label: 'Auto', value: 'auto' },
                  {
                    label: 'Force ASOne transcript.json',
                    value: 'force_asone',
                  },
                  { label: 'Force asr.json', value: 'force_assembly' },
                ]}
                label="Enhanced transcript selection"
                onChange={(_, value) => setEnhancedTranscriptMode(value as 'auto' | 'force_asone' | 'force_assembly')}
                value={enhancedTranscriptMode}
              />
              <small>Select which transcript to enhance</small>
            </div>

            <div className="mb-8">
              <Select
                disabled={fieldsDisabled || resubmitMutation.isPending}
                options={[
                  { label: 'Yes', value: '1' },
                  { label: 'No', value: '2' },
                ]}
                label="Force recreate preprocessor audio output"
                onChange={(_, value) => setForcePreprocessing(value === '1')}
                value={forcePreprocessing ? '1' : '2'}
              />
              <small>Force recreate preprocessor audio output for the job</small>
            </div>
          </DisclosureSection>
        </fieldset>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (fieldsDisabled || proxyModelsLoading || resubmitMutation.isPending || isSpeakerRangeInvalid) {
              return;
            }
            resubmitMutation.mutate({
              jobId: id,
              forceRetranscribe: hasTranscriptionOverrides,
              chosenTranscriptionModel:
                !asrProxyDisabled && transcriptionModel !== DEFAULT_ASR_MODEL_VALUE ? transcriptionModel : undefined,
              rushedJob: rushedJobProcessing,
              noShow: false,
              clearStylefixCache: clearChunksS3Cache,
              forcePreprocessing,
              useAssemblyTwoStageClustering: twoStageClusteringOverride,
              speakersExpected: supportsSpeakerCount && speakerCountMode === 'exact' ? speakersExpected : undefined,
              minSpeakersExpected:
                supportsSpeakerCount && speakerCountMode === 'range' ? minSpeakersExpected : undefined,
              maxSpeakersExpected:
                supportsSpeakerCount && speakerCountMode === 'range' ? maxSpeakersExpected : undefined,
              llmTranscriptSelectionMode: enhancedTranscriptMode,
            });
          }}
        >
          {submitError ? <ErrorMessage className="error-message mb-4" error={submitError} /> : null}

          <ModalFooter>
            <OutlinedButton type="button" onClick={onCancel}>
              Cancel
            </OutlinedButton>
            <Button
              type="submit"
              disabled={fieldsDisabled || proxyModelsLoading || isSpeakerRangeInvalid || resubmitMutation.isPending}
            >
              Resubmit
            </Button>
          </ModalFooter>
        </form>
      </div>
    </ModalWrapper>
  );
};
